import { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function MemoryGame() {
  const [sequence, setSequence] = useState<number[]>([]);
  const [player, setPlayer] = useState<number[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [flashCorrect, setFlashCorrect] = useState<number | null>(null);
  const [flashWrong, setFlashWrong] = useState<number | null>(null);

  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [showing, setShowing] = useState(false);
  const [started, setStarted] = useState(false);
  const [statusText, setStatusText] = useState("SELECT START TRAINING TO BEGIN SEQUENCE RECALL");
  const [statusType, setStatusType] = useState<"idle" | "memorizing" | "input" | "success" | "fail" | "gameover">("idle");

  const getRank = (s: number) => {
    if (s > 5000) return "S";
    if (s > 3000) return "A";
    if (s > 1500) return "B";
    if (s > 500) return "C";
    return "D";
  };

  const getRankLabel = (s: number) => {
    if (s > 5000) return "Master Learner";
    if (s > 3000) return "Advanced Learner";
    if (s > 1500) return "Skilled Student";
    if (s > 500) return "Active Learner";
    return "Beginner";
  };

  const generateSequence = (len: number) =>
    Array.from({ length: len }, () => Math.floor(Math.random() * 9));

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const playSequence = async (seq: number[]) => {
    setShowing(true);
    setStatusText("MEMORIZE SEQUENCE — OBSERVATION TIME ACTIVE");
    setStatusType("memorizing");
    await sleep(600);
    for (let i = 0; i < seq.length; i++) {
      setActive(seq[i]);
      await sleep(500);
      setActive(null);
      await sleep(300);
    }
    await sleep(400);
    setShowing(false);
    setStatusText(`INPUT SEQUENCE [0/${seq.length}] — AWAITING YOUR RECALL`);
    setStatusType("input");
  };

  const startGame = async () => {
    setLevel(1);
    setScore(0);
    setLives(3);
    setGameOver(false);
    setStarted(true);
    setPlayer([]);
    const seq = generateSequence(3);
    setSequence(seq);
    await playSequence(seq);
  };

  const nextLevel = async (newLevel: number) => {
    const seqLen = Math.min(newLevel + 2, 15);
    const seq = generateSequence(seqLen);
    setSequence(seq);
    setPlayer([]);
    await sleep(1500);
    await playSequence(seq);
  };

  const handleClick = async (index: number) => {
    if (showing || gameOver) return;

    const next = [...player, index];
    setPlayer(next);
    const step = next.length - 1;

    if (index !== sequence[step]) {
      setFlashWrong(index);
      await sleep(400);
      setFlashWrong(null);

      const newLives = lives - 1;
      setLives(newLives);

      if (newLives <= 0) {
        setGameOver(true);
        setStatusText("Session Complete — Assessment Closed");
        setStatusType("gameover");
      } else {
        setStatusText(`✗ MISMATCH DETECTED · ${newLives} BRAIN ATTEMPTS REMAINING`);
        setStatusType("fail");
        await sleep(1200);
        setPlayer([]);
        setStatusText(`INPUT SEQUENCE — RETRY AUTHORIZED`);
        setStatusType("input");
      }
      return;
    }

    setFlashCorrect(index);
    await sleep(250);
    setFlashCorrect(null);

    if (next.length === sequence.length) {
      const gained = level * 100;
      const newScore = score + gained;
      const newLevel = level + 1;
      setScore(newScore);
      setLevel(newLevel);
      setStatusText(`✓ SEQUENCE CONFIRMED · +${gained} SCORE · ADVANCING TO LEVEL ${newLevel}`);
      setStatusType("success");
      await nextLevel(newLevel);
    } else {
      setStatusText(`INPUT SEQUENCE [${next.length}/${sequence.length}] — PROCESSING`);
      setStatusType("input");
    }
  };

  const rank = getRank(score);
  const rankLabel = getRankLabel(score);

  const statusColor: Record<string, string> = {
    idle: "text-stone-500",
    memorizing: "text-amber-600",
    input: "text-amber-800",
    success: "text-emerald-600",
    fail: "text-rose-600",
    gameover: "text-rose-700",
  };

  return (
    <div
      className="min-h-screen bg-[#FAF6EF] text-stone-800 flex items-center justify-center p-4 md:p-8"
      style={{ fontFamily: "'Baloo 2', cursive, system-ui" }}
    >
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl border border-[#A67C52]/10 p-6 md:p-8 relative overflow-hidden transition-all duration-300">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-4 border-b border-[#FAF6EF]">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#A67C52] font-semibold mb-0.5">
              BharatPariksha Cognitive Training
            </p>
            <h1 className="text-3xl font-extrabold tracking-wide text-[#8B5E34]">
              Brain Training Lab
            </h1>
          </div>
          <button
            onClick={startGame}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#8B5E34] text-white font-bold rounded-xl shadow-md hover:bg-[#A67C52] transition-all transform active:scale-95"
          >
            {started ? "↺ Restart Session" : "▶ Start Training"}
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Level", value: level, custom: false },
            { label: "Score", value: score.toLocaleString(), custom: false },
            { label: "Lives", value: null, custom: true },
            { label: "Rank", value: rank, custom: true },
          ].map((stat, i) => (
            <div key={i} className="bg-[#FAF6EF] rounded-2xl p-4 border border-[#A67C52]/5 flex flex-col justify-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#A67C52] mb-1">
                {stat.label}
              </p>
              {!stat.custom ? (
                <p className="text-2xl font-bold text-[#8B5E34]">{stat.value}</p>
              ) : stat.label === "Lives" ? (
                <div className="flex gap-1.5 text-2xl items-center h-8">
                  {Array.from({ length: 3 }).map((_, li) => (
                    <span
                      key={li}
                      className={`transition-colors duration-300 ${
                        li < lives ? "text-rose-500 animate-pulse" : "text-stone-300"
                      }`}
                    >
                      ♥
                    </span>
                  ))}
                </div>
              ) : (
                <div className="min-h-[2.5rem] flex flex-col justify-center">
                  <p className="text-2xl font-black text-[#8B5E34] leading-none">{stat.value}</p>
                  <p className="text-[11px] font-semibold text-[#A67C52] mt-0.5 uppercase tracking-tight">
                    {rankLabel}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Progress Tracker */}
        <div className="mb-4">
          <div className="mb-1.5 flex justify-between text-xs font-medium text-[#A67C52] uppercase tracking-wider">
            <span>Sequence Progress</span>
            <span>
              {player.length} / {sequence.length}
            </span>
          </div>
          <div className="h-3 bg-[#FAF6EF] rounded-full overflow-hidden p-0.5 border border-[#A67C52]/10">
            <div
              className="h-full bg-gradient-to-r from-[#8B5E34] to-[#A67C52] rounded-full transition-all duration-300"
              style={{
                width: sequence.length > 0 ? `${(player.length / sequence.length) * 100}%` : "0%",
              }}
            />
          </div>
        </div>

        {/* Sequence Dot Indicators */}
        <div className="flex justify-center gap-2 flex-wrap mb-6">
          {sequence.map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full border transition-all duration-300 ${
                i < player.length
                  ? "bg-emerald-500 border-emerald-600"
                  : i === player.length && !showing
                  ? "bg-amber-400 border-amber-500 scale-110 shadow-sm"
                  : "bg-white border-[#A67C52]/30"
              }`}
            />
          ))}
        </div>

        {/* Interactive Game Grid */}
        <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto mb-6">
          {Array.from({ length: 9 }).map((_, idx) => (
            <motion.button
              key={idx}
              whileTap={{ scale: 0.94 }}
              onClick={() => handleClick(idx)}
              disabled={showing || gameOver}
              className={`aspect-square rounded-2xl border-2 font-bold text-2xl transition-all duration-200 relative overflow-hidden flex items-center justify-center
                ${
                  active === idx
                    ? "bg-amber-400 border-amber-500 text-stone-900 shadow-md scale-105"
                    : flashCorrect === idx
                    ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                    : flashWrong === idx
                    ? "bg-rose-50 border-rose-500 text-rose-700"
                    : "bg-[#FAF6EF] border-[#A67C52]/20 text-[#8B5E34] hover:bg-white hover:border-[#8B5E34] hover:shadow-sm"
                }
                ${showing || gameOver ? "cursor-not-allowed opacity-80" : "cursor-pointer"}
              `}
            >
              {idx + 1}
            </motion.button>
          ))}
        </div>

        {/* Live Tracking Status Panel */}
        <div className="text-center min-h-[44px] bg-[#FAF6EF] rounded-xl flex items-center justify-center p-3 mb-2 border border-[#A67C52]/5">
          <p className={`text-sm font-semibold tracking-wide ${statusColor[statusType]}`}>
            {statusType === "memorizing" || statusType === "input" ? "● " : ""}
            {statusText}
          </p>
        </div>

        {/* Game Over Screen with Sequence Review Box */}
        {gameOver && (
          <div className="border-t-2 border-dashed border-[#A67C52]/20 pt-6 mt-4 text-center animate-fadeIn">
            <h2 className="text-3xl font-extrabold text-rose-600 tracking-wide mb-1">Session Complete</h2>
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-4">
              BharatPariksha Progress Assessment Report
            </p>
            
            <div className="bg-[#FAF6EF] border-2 border-[#8B5E34]/20 rounded-2xl inline-flex flex-col items-center px-10 py-4 mb-5 shadow-inner">
              <span className="text-xs text-[#A67C52] font-bold uppercase tracking-widest mb-1">Final Performance Rank</span>
              <span className="text-5xl font-black text-[#8B5E34]">{rank}</span>
              <span className="text-xs text-stone-600 font-bold mt-1 uppercase tracking-wide">{rankLabel}</span>
            </div>

            {/* 🎯 NEW: Sequence Review Analytics Section */}
            <div className="w-full max-w-md mx-auto bg-[#FAF6EF] rounded-2xl p-4 border border-[#A67C52]/10 mb-5 text-left">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#8B5E34] mb-3 text-center border-b border-[#A67C52]/10 pb-1.5">
                Review Your Sequence Numbers
              </h3>
              
              <div className="space-y-3">
                {/* Correct Sequence Display */}
                <div>
                  <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <span>✓</span> Sahi Sequence (Target):
                  </p>
                  <div className="flex gap-1.5">
                    {sequence.map((num, idx) => (
                      <span 
                        key={idx} 
                        className="w-7 h-7 bg-white text-emerald-700 border border-emerald-300 font-bold rounded-lg flex items-center justify-center text-sm shadow-sm"
                      >
                        {num + 1}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Player Input Sequence Display */}
                <div>
                  <p className="text-[11px] font-bold text-rose-700 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <span>✗</span> Aapka Sequence (Your Input):
                  </p>
                  <div className="flex gap-1.5">
                    {player.length === 0 ? (
                      <span className="text-xs italic text-stone-400">No input registered.</span>
                    ) : (
                      player.map((num, idx) => {
                        const isCorrect = num === sequence[idx];
                        return (
                          <span 
                            key={idx} 
                            className={`w-7 h-7 font-bold rounded-lg flex items-center justify-center text-sm shadow-sm border ${
                              isCorrect 
                                ? "bg-white text-emerald-600 border-emerald-200" 
                                : "bg-rose-50 text-rose-700 border-rose-300 font-black"
                            }`}
                          >
                            {num + 1}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center gap-6 text-sm font-medium text-stone-600 bg-[#FAF6EF] max-w-sm mx-auto p-3 rounded-xl mb-4">
              <p>Final Score: <span className="text-[#8B5E34] font-bold">{score.toLocaleString()}</span></p>
              <div className="w-1 h-4 bg-stone-300 rounded-full" />
              <p>Level Reached: <span className="text-[#8B5E34] font-bold">{level}</span></p>
            </div>
            
            <button 
              onClick={startGame}
              className="px-8 py-3 bg-[#8B5E34] text-white font-bold rounded-xl shadow-md hover:bg-[#A67C52] hover:shadow-lg transition-all transform active:scale-95"
            >
              Restart Training Session
            </button>
          </div>
        )}

        {/* Footer Area */}
        <div className="mt-6 pt-4 border-t border-[#FAF6EF] flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-[11px] font-medium tracking-wide text-[#A67C52]">
            Self-Paced Cognitive Enhancement System
          </p>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <p className="text-[11px] font-bold tracking-wide text-emerald-700">System Ready</p>
          </div>
        </div>
      </div>
    </div>
  );
}